import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import EmailIcon from "@mui/icons-material/Email";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import TelegramIcon from "@mui/icons-material/Telegram";
import {Box, Button, Menu, styled} from "@mui/material";
import React, {useState} from "react";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import {ContactInterface} from "../../interfaces/ContactInterface";
import ArticleIcon from "@mui/icons-material/Article";
import DownloadIcon from "@mui/icons-material/Download";

interface TeamMemberInterface {
    avatar: string;
    title: string;
    description: string;
    contacts: ContactInterface[];
    docInDesc?: { name: string,title: string, file: string };
}

const TeamMemberContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
});


const TeamMemberAvatar = styled(Box)({
    height: "400px",
    width: "auto",
    aspectRatio: "1/1",
    borderRadius: "100%",
    maxHeight: "80vw"
});

const TeamMemberTitle = styled(Box)({
    marginTop: "25px",
    fontSize: "2.2rem",
    fontWeight: 500,
    color: "#FFFFFF",
    zIndex: 2,
    textShadow: "1px 1px 2px black, 0 0 25px black, 0 0 5px black",
});

const TeamMemberDescription = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    textAlign: "center",
    fontWeight: 400,
    fontSize: "1.3rem",
    color: "#FFFFFF",
    textShadow: "1px 1px 2px black, 0 0 25px black, 0 0 5px black",
});

const ContactsContainer = styled(Box)({
    padding: "10px 20px",
    boxSizing: "border-box",
    backgroundSize: "cover",
    "& ul":{
        "@media(max-width: 768px)":{
            padding: 0,
        },
    },
    "& li":{
        display: "grid",
        width: "max-content",
        gridTemplateColumns: "200px auto",
        alignItems: "center",
        gap: "20px",
        marginTop: "10px",
        fontSize: "1.3rem",
        fontWeight: 200,
        color: "#FFFFFF",
        zIndex: 2,
        textShadow: "1px 1px 2px black, 0 0 25px black, 0 0 5px black",
        "@media(max-width: 768px)":{
            gridTemplateColumns: "1fr",
            fontSize: "1rem",
            gap: "10px",
        },
    },
    "@media(max-width: 768px)":{
        gridTemplateColumns: "1fr",
    },
});

const ContactsButton = styled(Button)({
    background: "#FFFFFF",
    color: "#000000",
    display: "flex",
    gap: "10px",
    alignItems: "center",
    textDecoration: "none",
    textShadow: "none",
    transition: "background 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), transform 0.3s ease-out",
    "&:hover":{
        background: "#ffffff",
        boxShadow:"0 0 15px #ffc371",
        transform: "scale(1.05)",
    },
    "@media(max-width: 400px)":{
        fontSize: "0.6rem",
    },
});

const TeamMember = ({avatar, title, description, contacts, docInDesc}: TeamMemberInterface): JSX.Element => {
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLAnchorElement | HTMLButtonElement>(null);
    
    const getIcon = (iconType: string):React.ReactNode => {
        switch (iconType) {
        case "email": return <EmailIcon htmlColor={"#000000"}/>;
        case "phone": return <LocalPhoneIcon htmlColor={"#000000"}/>;
        case "linkedin": return <LinkedInIcon htmlColor={"#000000"}/>;
        case "github": return <GitHubIcon htmlColor={"#000000"}/>;
        case "telegram": return <TelegramIcon htmlColor={"#000000"}/>;
        case "event": return <EventAvailableIcon htmlColor={"#000000"}/>;
        case "doc": return <ArticleIcon htmlColor={"#000000"}/>;
        default: return <EmailIcon htmlColor={"#000000"}/>;
        }
    };
    
    const onDownload = ():void => {
        if(docInDesc) {
            const anchor = document.createElement("a");
            anchor.href = docInDesc.file;
            anchor.download = docInDesc.name;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        }
    };
    
    const renderDocInDesc = ():React.ReactNode => {
        if(docInDesc){
            return <ContactsButton  onClick={onDownload}>
                {getIcon("doc")}
                {docInDesc.title}
                <DownloadIcon htmlColor={"#000000"}/>
            </ContactsButton>;
        }
        return null;
    };
    
    return (
        <TeamMemberContainer>
            <TeamMemberAvatar sx={{
                background: `url(${avatar}) no-repeat center, rgba(0, 0, 0, 0.6)`,
                backgroundSize: "cover",
            }} />
            <TeamMemberTitle>
                {title}
            </TeamMemberTitle>
            <TeamMemberDescription>
                {description}
                {renderDocInDesc()}
            </TeamMemberDescription>
            <ContactsContainer>
                <ul>
                    {contacts.slice(0,3).map((opt, index) => <li key={title + "-contact-" + index}>{opt.key}:
                        <ContactsButton  onClick={() => window.open(opt.link)}>
                            {getIcon(opt.iconType)}
                            {opt.value}
                            <DoubleArrowIcon htmlColor={"#000000"}/>
                        </ContactsButton>
                    </li>
                    )}
                </ul>
            </ContactsContainer>
            {contacts.length > 3 && <ContactsButton
                onClick={(e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}>
                See More Contact Details
                <ReadMoreIcon htmlColor={"#000000"}/>
            </ContactsButton>}
            <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                <ContactsContainer>
                    <ul>
                        {contacts.slice(3,10).map((opt, index) => <li key={title + "-contact-" + index}>{opt.key}:
                            <ContactsButton  onClick={() => window.open(opt.link)}>
                                {getIcon(opt.iconType)}
                                {opt.value}
                                <DoubleArrowIcon htmlColor={"#000000"}/>
                            </ContactsButton>
                        </li>
                        )}
                    </ul>
                </ContactsContainer>
            </Menu>
        </TeamMemberContainer>
    );
};

export default TeamMember;
